/**
 * COLOR / -COLOR / SETCOLOR / ЦВЕТ command-line parser — the CECOLOR
 * current-entity-colour setter (AutoCAD parity).
 *
 * AutoCAD's COLOR command (aliases COL / -COLOR / SETCOLOR; RU ЦВЕТ) sets the
 * CURRENT colour — the `$CECOLOR` system variable — that NEWLY-created entities
 * inherit. Two call shapes (AutoCAD parity):
 *
 *   COLOR 5                  — set current colour to ACI 5 (and the value forms
 *   ЦВЕТ #ff8000               #hex / R,G,B / ByLayer / ByBlock / 0 / 256)
 *   SETCOLOR 255,128,0
 *   COLOR / SETCOLOR / ЦВЕТ  — NO value → open the AutoCAD "Select Color" dialog
 *                              (the AciColorPicker), exactly like AutoCAD's COLOR.
 *   -COLOR                   — NO value → the command-line keyword prompt
 *                              "[Truecolor/COlorbook] <ByLayer>" (AutoCAD -COLOR
 *                              parity — the dialog-less variant).
 *
 * This parser is PURE — it does NOT mutate the store. It returns a discriminated
 * result the App command resolver acts on (dispatch `header/set-cecolor`, open
 * the picker, or start the keyword prompt). Geometry/model state is only ever
 * changed by the reducer the `header/set-cecolor` command flows into — never
 * here — and the AI / ribbon / swatch all route through the SAME command
 * (Command-Engine one-truth, invertible undo).
 *
 * Returns `{ handled: false }` when the input is NOT a COLOR command so the
 * caller continues with normal geometry-command resolution.
 */

import {
  parseColorCommandValueStrict,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";

export type ColorCommandResult =
  | { handled: false }
  | {
      /** Matched a COLOR command with a value → dispatch `header/set-cecolor`. */
      handled: true;
      kind: "set";
      color: EntityColor;
      message: string;
    }
  | {
      /** Matched COLOR/SETCOLOR/ЦВЕТ with no value → open the Select Color dialog. */
      handled: true;
      kind: "openPicker";
      message: string;
    }
  | {
      /** Matched -COLOR with no value → the CLI keyword prompt (no dialog). */
      handled: true;
      kind: "cliPrompt";
      message: string;
    }
  | {
      /**
       * Matched a COLOR command but the VALUE was NOT a valid colour
       * (e.g. `COLOR blu`, `COLOR 999`). AutoCAD answers `"Invalid color."`
       * and RE-PROMPTS rather than coercing to a fallback — so the command IS
       * handled (it is unambiguously a COLOR command), but the resolver must NOT
       * dispatch a `header/set-cecolor`; it surfaces the error + re-prompts.
       */
      handled: true;
      kind: "invalid";
      message: string;
    };

/**
 * STRICT validation of a COLOR-command VALUE — the ONE-truth strict colour-value
 * validator {@link parseColorCommandValueStrict} (drawing-model), re-exported
 * here under the local name the App resolver / tests already import. It is
 * distinct from the PERMISSIVE `coerceColor` migration codec (which maps an
 * unknown string → ByLayer and an arbitrary int → an out-of-range index, neither
 * acceptable for live user input): AutoCAD's COLOR command REJECTS an invalid
 * value and re-prompts `"Invalid color."` — it never silently resets to ByLayer
 * or stores a `62=999` the export would emit invalid. Accepts EXACTLY the
 * AutoCAD-valid forms (ByLayer/ByBlock, ACI 1..255, #rrggbb, R,G,B) and returns
 * `null` for everything else (caller surfaces the error).
 *
 * Sharing the one drawing-model validator means the AI `SET_CURRENT_COLOR`
 * structured-action seam enforces the IDENTICAL accept-list (no per-surface
 * strict parser — jscpd one-truth, CLAUDE.md priority #1). The migration codec
 * stays for the load / DXF-round-trip path (legacy data may legitimately carry a
 * negative layer-off ACI); the COMMAND path is strict.
 */
export const parseColorCommandValue: (raw: string) => EntityColor | null =
  parseColorCommandValueStrict;

// COLOR / COL / -COLOR / SETCOLOR / ЦВЕТ [value] — shared regex for the
// command-word match, reused by `tryHandleColorCommand` (interactive) and
// `parseColorSetCommand` (the store / batch / AI seam). See the literal below.
//
// ReDoS analysis: a fixed command-word alternation followed by an optional
// bounded value (`.{0,255}`); no nested quantifier, no alternation overlap →
// linear matching time.
// eslint-disable-next-line security/detect-unsafe-regex -- bounded, no backtracking
const COLOR_SET_RE = /^(-?)(COLOR|COL|SETCOLOR|ЦВЕТ)(?:\s+([^\s].{0,255}))?$/iu;

/**
 * Pure store-path parser for the COLOR-SET VALUE form (`COLOR 5` / `ЦВЕТ
 * 255,128,0` / `-COLOR #ff8000`). Returns the strictly-validated
 * {@link EntityColor} the caller dispatches as `header/set-cecolor`, or `null`
 * when the text is NOT a value-form COLOR command (a non-COLOR command, OR a
 * bare `COLOR`/`-COLOR` with no value — the no-value picker/CLI-prompt forms are
 * UI-only and stay in the App resolver), OR the value is invalid.
 *
 * This is the SHARED seam (FINDING 4 one-truth): `storeHelpers`'
 * `resolveGeometryDocumentCommand` calls it inside the `commandline/submit`
 * branch so the store-level `executeBatchCommands` path AND AI text submissions
 * route COLOR-SET through the SAME parse the interactive `submitCommandText`
 * uses — without reaching into React state. Pure (no i18n, no store): the caller
 * messages on `null`.
 */
export function parseColorSetCommand(text: string): EntityColor | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const m = COLOR_SET_RE.exec(trimmed);
  if (!m) return null;
  const valueRaw = m[3];
  if (valueRaw === undefined) return null; // bare COLOR/-COLOR — UI-only forms
  return parseColorCommandValue(valueRaw.trim());
}

/**
 * @param text    the raw command-line text.
 * @param current the live CURRENT colour ($CECOLOR). Threaded in so the
 *                dialog-less `-COLOR` keyword prompt shows the AutoCAD-correct
 *                `<default>` — the current colour, NOT a hardcoded `<ByLayer>`
 *                (AutoCAD: `Enter color [Truecolor/COlorbook] <5>:` when the
 *                current colour is ACI 5; pressing Enter KEEPS the current
 *                colour). Pure — no store access here.
 */
export function tryHandleColorCommand(
  text: string,
  current: EntityColor,
): ColorCommandResult {
  const trimmed = text.trim();
  if (!trimmed) return { handled: false };

  const m = COLOR_SET_RE.exec(trimmed);
  if (!m) return { handled: false };

  const dash = m[1] === "-";
  const valueRaw = m[3]; // group 3 — the optional value

  if (valueRaw === undefined) {
    // No value. AutoCAD: COLOR/SETCOLOR/ЦВЕТ open the Select Color dialog;
    // -COLOR runs the dialog-less command-line keyword prompt with the CURRENT
    // colour as the `<default>` (Enter keeps it).
    if (dash) {
      return {
        handled: true,
        kind: "cliPrompt",
        message: t("colorcmd.prompt", { current: describeColor(current) }),
      };
    }
    return {
      handled: true,
      kind: "openPicker",
      message: t("colorcmd.open_picker"),
    };
  }

  // With a value — STRICTLY validate the accepted forms (ByLayer/ByBlock,
  // ACI 1..255, #rrggbb, R,G,B). AutoCAD parity: an invalid value is REJECTED
  // ("Invalid color.") and re-prompted, NOT coerced to a ByLayer fallback or an
  // out-of-range index the export would emit invalid (FINDING 2).
  const color = parseColorCommandValue(valueRaw.trim());
  if (color === null) {
    return {
      handled: true,
      kind: "invalid",
      message: t("colorcmd.invalid"),
    };
  }
  return {
    handled: true,
    kind: "set",
    color,
    message: t("colorcmd.feedback", { value: describeColor(color) }),
  };
}

/**
 * Parse a `-COLOR` keyword-prompt reply (`Truecolor` / `COlorbook` / a value /
 * empty=keep-current). Returns the resolved colour, or `null` to re-prompt.
 * Mirrors AutoCAD's `-COLOR` prompt: an empty Enter accepts the `<default>` —
 * the CURRENT colour (`current`), KEEPING it (NOT resetting to ByLayer, which
 * would silently wipe a user's current colour on the natural "accept default"
 * reflex); a value (ACI / #hex / R,G,B) is coerced; the `Truecolor` keyword
 * expects a following triple but, typed alone, simply re-prompts for the triple
 * (the value form `255,128,0` is the canonical entry); `COlorbook` is DEFERRED
 * (matches the bookColor-no-palette deferral) → re-prompt. An invalid value
 * (`blu` / `999`) STRICTLY re-prompts (returns `null`) rather than coercing to a
 * ByLayer fallback — AutoCAD's `-COLOR` "Invalid color." re-prompt (FINDING 2).
 */
export function resolveColorPromptReply(
  reply: string,
  current: EntityColor,
): EntityColor | null {
  const trimmed = reply.trim();
  if (trimmed === "") return current; // <default> = keep the current colour
  const lower = trimmed.toLowerCase();
  // Bare `Truecolor` / `T` / `Ц` keyword with no triple → re-prompt for the
  // R,G,B value (the Truecolor path needs the triple).
  if (lower === "truecolor" || lower === "t" || lower === "ц") return null;
  // `COlorbook` / `CO` keyword — colour books are deferred; re-prompt.
  if (lower === "colorbook" || lower === "co") return null;
  // STRICT value validation — `null` (invalid) re-prompts instead of coercing.
  return parseColorCommandValue(trimmed);
}

/**
 * Human-readable description of a current colour for the command feed. Bilingual
 * labels for the symbolic cases; raw value for index / true-colour. Exported so
 * the App command resolver renders the `-COLOR` reply feedback through the SAME
 * localized labels (not the raw typed text).
 */
export function describeColor(color: EntityColor): string {
  switch (color.kind) {
    case "byLayer":
      return t("colorcmd.bylayer");
    case "byBlock":
      return t("colorcmd.byblock");
    case "index":
      return String(color.aci);
    case "rgb":
      return `${color.r},${color.g},${color.b}`;
    case "bookColor":
      return color.name;
  }
}
